import React, { useEffect } from "react";
import Popup from "reactjs-popup";
import { DataGrid } from "@mui/x-data-grid";
import { Chip } from "@material-ui/core";
import { Tooltip, Box } from "@mui/material";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { isEmpty, isNull } from "lodash";
import SysGuardLocationMap from "../Dashboard/Components/SysGuardLocationMap";
import {
  getCountryCode,
  getFlagUrl,
  renderLocationClassName,
  getDataSourceIcon,
} from "../Dashboard/ContentPages/SysGuardColumnConfig";
import CustomBarChart from "../Dashboard/Components/CustomBarChart";
import { DeviceChart } from "../Dashboard/Components/DeviceChart";
import {
  applicationPopupMainTitleLocations,
  cityBasedLocations,
  countryWithFlagTableLocations,
  customBarChatLocations,
  maskedUsernameLoc,
  noSecondTitleLocations,
  SET_SYS_GUARD_ALERT_INFO_DATA,
  sysGuardAlertInfoDataColumns,
  sysGuardPopupTitles,
  sysGuardSubHeadings,
  sysGuardTitleNumbers,
  titleWithCounts,
  unknownUser,
} from "../sysGuardConstants";
import SysGuardCountryMap from "../Dashboard/Components/SysGuardCountryMap";
import SysGuardLineGraph from "../Dashboard/Components/SysGuardLineGraph";
import SysGuardInfoBox from "../Dashboard/Components/SysGuardInfoBox";
import { useDispatch, useSelector } from "react-redux";
import { maskUsername } from "../SysGuardCommonComp";
import { SysGuardAlertButtons } from "../SysGuardHelperComponents";
import { useMediaQuery } from "material-ui-core";

const renderPopupTitle = (location, popupData) => {
  console.log(location, "loc");
  if (location === "userBasedFailurePercentage") {
    return `User - ${popupData?.userName || "Unknown User"} (${popupData?.failedAccess || 0})`;
  } else if (location === "locationBasedFailurePercentage") {
    return `Location - ${popupData?.countryName || "Unknown Location"} (${popupData?.failedAccess || 0})`;
  } else if (location === "cityBasedFailurePercentage") {
    return `City - ${popupData?.cityName || "Unknown City"} (${popupData?.failedAccess || 0})`;
  } else if (location === "applicationBasedFailurePercentage") {
    return `Application - ${popupData?.applicationName || "Unknown Application"} (${popupData?.failedAccess || 0})`;
  }
  return sysGuardPopupTitles[location] || "Behavioral Anomalies";
};

const renderTitleNumber = (loc, popupData) =>
  popupData?.[sysGuardTitleNumbers[loc]] ?? popupData?.applicationsInUse;

const renderTitleNumberClasses = (loc, data) => {
  if (titleWithCounts.includes(loc)) {
    return "sys-guard-location nonAnomalies";
  } else {
    return renderLocationClassName(data);
  }
};

const renderSubHeading = (loc) => sysGuardSubHeadings[loc] || "Anomalies:";

const renderCountryNameWithFlag = (country, city, loc) => {
  const code = getCountryCode(country);
  const flagUrl = code ? getFlagUrl(code) : null;
  return (
    <>
      {flagUrl && country !== "Unknown" ? (
        <div className="country-with-flag">
          <img src={flagUrl} title={country} alt={country} className="country-flag Title" />
          <span className="second-title">{cityBasedLocations.includes(loc) ? city : country}</span>
        </div>
      ) : (
        <div className="country-with-flag">
          <img
            src={"/sysGuardIcons/unknownFlag.svg"}
            title={"Unknown"}
            alt={"Unknown"}
            className="country-flag Title"
          />
          <span className="second-title">{cityBasedLocations.includes(loc) ? city : country}</span>
        </div>
      )}
    </>
  );
};

const renderMainPopupTitle = (
  loc,
  popupData,
  isIncognito,
  sysGuardDataSource,
) => {
  if (applicationPopupMainTitleLocations.includes(loc)) {
    return popupData?.applicationName || "Unknown";
  } else {
    const username = isIncognito
      ? maskUsername(popupData?.userName)
      : popupData?.userName || "Unknown User";
    const datasourceId = popupData?.dataSourceId;
    const ds = datasourceId ? sysGuardDataSource?.[datasourceId] : null;

    return (
      <div className="ds-col-container">
        <span>{username}</span>
        {ds && (
          <Chip
            title={ds?.domainName}
            icon={
              <img
                src={getDataSourceIcon(ds?.typeCode)}
                alt={ds?.typeName}
                className="data-source-chip-icon"
              />
            }
            label={ds?.domainName}
            className="data-source-chip"
            size="small"
          />
        )}
      </div>
    );
  }
};

const UserLocationHeader = ({
  popupData,
  loc,
  isIncognito,
  sysGuardDataSource,
  countryName,
}) => (
  <div className="user-location-left-panel">
    <div className="header-title">{renderPopupTitle(loc, popupData)}</div>
    {noSecondTitleLocations.includes(loc) ? (
      <div className="second-title-container">
        <div className="second-title-mini-container">
          <span className="header-title">
            {" "}
            {`( Failed Attempts: ${popupData?.failedAccess || 0} | Total Attempts: ${popupData?.totalAccess || 0}`}{" "}
          </span>
          <span className="header-title">)</span>
        </div>
      </div>
    ) : (
      <div className="second-title-container">
        {countryWithFlagTableLocations.includes(loc) ? (
          renderCountryNameWithFlag(popupData?.countryName || countryName, popupData?.cityName, loc)
        ) : (
          <span className="second-title">
            {renderMainPopupTitle(
              loc,
              popupData,
              isIncognito,
              sysGuardDataSource,
            )}
          </span>
        )}
        <div className="second-title-mini-container">
          <span className="header-title"> {`( ${renderSubHeading(loc)}`} </span>
          <span
            className={renderTitleNumberClasses(
              loc,
              renderTitleNumber(loc, popupData),
            )}
          >
            {renderTitleNumber(loc, popupData)}
          </span>
          <span className="header-title">)</span>
        </div>
      </div>
    )}
  </div>
);

const handleRowClick = (data, dispatch, loc) => {
  if (sysGuardAlertInfoDataColumns.includes(loc)) {
    dispatch({ type: SET_SYS_GUARD_ALERT_INFO_DATA, payload: data });
  }
};

const getPopupClasses = (loc) => {
  if (loc === "locationBasedAnomalies" || loc === "cityBasedAnomalies") {
    return "sys-guard-table-pop-up locationAnomaly";
  } else if (loc === "locationBasedApplications" || loc === "cityBasedApplications") {
    return "sys-guard-table-pop-up locationApplication";
  } else {
    return "sys-guard-table-pop-up";
  }
};

const UserLocationTable = ({
  sysGuardPopupData,
  totalElements,
  pagination,
  setPagination,
  tableColumns,
  dispatch,
  loc,
  isSmall,
}) => (
  <div className={getPopupClasses(loc)}>
    <DataGrid
      rows={
        !isNull(sysGuardPopupData)
          ? sysGuardPopupData.map((row, index) => ({ ...row, id: index }))
          : []
      }
      columns={tableColumns()}
      rowCount={totalElements}
      disableSelectionOnClick
      pagination
      paginationMode="server"
      paginationModel={pagination}
      onPaginationModelChange={setPagination}
      pageSizeOptions={[5, 10, 20, 50, 100]}
      disableColumnFilter
      disableColumnSelector
      disableColumnMenu
      disableRowSelectionOnClick={!sysGuardAlertInfoDataColumns.includes(loc)}
      localeText={{ noRowsLabel: "No Data Available" }}
      sx={{
        maxHeight: 631,
        maxWidth: 700,
        overflowY: "auto",
        "& .MuiDataGrid-virtualScroller": {
          overflowY: "auto",
        },
      }}
      getRowHeight={() => "auto"}
      getRowClassName={(params) =>
        loc === "userDevices" && params.row.alertInfo ? "highlight-row" : ""
      }
      onRowClick={(params) => handleRowClick(params.row, dispatch, loc)}
    />
  </div>
);

const renderSidePopupData = (data, loc, isIncognito) => {
  if (!data || data.length === 0) return null;
  const sortedData = [...data].sort((a, b) => b.count - a.count).slice(0, 4);

  if (noSecondTitleLocations.includes(loc)) {
    const rows = sortedData.map((item, index) => ({
      id: index + 1,
      name:
        isIncognito && maskedUsernameLoc.includes(loc)
          ? maskUsername(item.name)
          : unknownUser(item.name),
      count: item.count,
    }));

    const columns = [
      {
        field: "name",
        headerName: "Failure Reason",
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Tooltip
            title={params.value}
            placement="top-start"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#2f2f2f",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  padding: "10px 12px",
                  borderRadius: "8px",
                  maxWidth: "320px",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                },
              },
              arrow: {
                sx: {
                  color: "#2f2f2f",
                },
              },
            }}
          >
            <div className="failure-reason-cell">{params.value}</div>
          </Tooltip>
        ),
      },
      {
        field: "count",
        headerName: "Count",
        width: 70,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div className="count-badge">{params.value}</div>
        ),
      },
    ];

    return (
      <div className="failure-table-wrapper">
        <DataGrid
          rows={rows}
          columns={columns}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          rowHeight={44}
          headerHeight={42}
          sx={{
            border: "none",
            background: "transparent",

            "& .MuiDataGrid-main": {
              border: "none",
            },

            "& .MuiDataGrid-columnHeaders": {
              background: "#f7f7f8",
              borderRadius: "10px",
              border: "none",
              fontSize: "13px",
              fontWeight: 600,
              color: "#555",
            },

            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f1f1f1",
              fontSize: "13px",
              color: "#222",
              display: "flex",
              alignItems: "center",
              overflow: "visible",
            },

            "& .MuiDataGrid-row": {
              background: "#fff",
              transition: "all 0.2s ease",
            },

            "& .MuiDataGrid-row:hover": {
              background: "#fafafa",
            },

            "& .MuiDataGrid-virtualScroller": {
              overflowX: "hidden",
            },

            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },

            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
          }}
        />
      </div>
    );
  }

  const totalCount = sortedData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="side-popup-container">
      {sortedData.map((item, index) => {
        const percentage = (item.count / totalCount) * 100;

        return (
          <div key={index} className="side-popup-item">
            <span
              className="side-popup-name"
              title={
                isIncognito && maskedUsernameLoc.includes(loc)
                  ? maskUsername(item.name)
                  : unknownUser(item.name)
              }
            >
              {isIncognito && maskedUsernameLoc.includes(loc)
                ? maskUsername(item.name)
                : unknownUser(item.name)}
            </span>

            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const renderSidePopupDataTitle = (location) => {
  if (location === "userLocations" || location === "locationBasedUsers" || location === "cityBasedUsers") {
    return "Locations";
  } else if (location === "userDevices" || location === "totalDevices") {
    return "No of Devices";
  } else if (
    location === "userApplications" ||
    location === "locationBasedApplications"|| location === "cityBasedApplications"
  ) {
    return "Applications";
  } else if (
    location === "applicationBasedUsers" ||
    location === "locationBasedAnomalies" ||
    location === "cityBasedAnomalies" ||
    location === "applicationBasedAnomalies"
  ) {
    return "Users";
  } else if (
    location === "locationBasedFailurePercentage" ||
    location === "userBasedFailurePercentage" ||
    location === "applicationBasedFailurePercentage" ||
    location === "cityBasedFailurePercentage"
  ) {
    return "Top 4 Failure Reasons";
  } else {
    return "Policies";
  }
};

const renderSideCharts = (
  location,
  sidePopupData,
  sysGuardPopupData,
  country,
  alertInfoData,
  isIncognito,
  dataSourceId,
  popupData,
  countryName,
) => {
  const parentData = {
    dataSourceId: dataSourceId,
    actorId: popupData?.userId,
    loc: location,
    country: popupData?.countryName || countryName,
    application: popupData?.applicationName,
    currentPage: 0,
    pageSize: 10,
  };
  if (
    (location === "userDevices" || location === "totalDevices") &&
    sidePopupData
  ) {
    return DeviceChart(sidePopupData);
  } else if (
    location === "userLocations" ||
    noSecondTitleLocations.includes(location)
  ) {
    return (
      <div className={noSecondTitleLocations.includes(location) ? "sys-guard-location-side-chart failure" : "sys-guard-location-side-chart"}>
        {renderSidePopupData(sidePopupData, location, isIncognito)}
        <SysGuardLocationMap
          loc={
            location === "locationBasedFailurePercentage" ||
            location === "userBasedFailurePercentage" ||
            location === "cityBasedFailurePercentage" ||
            location === "applicationBasedFailurePercentage"
              ? "locationBasedFailurePercentage"
              : "userLocations"
          }
        />
      </div>
    );
  } else if (customBarChatLocations.includes(location) && sidePopupData) {
    return (
      <div className="sys-guard-location-side-chart application">
        {renderSidePopupData(sidePopupData, location, isIncognito)}
        <CustomBarChart applications={sidePopupData} isUsageFrequency />
      </div>
    );
  } else if (
    ( location === "locationBasedUsers" || location === "cityBasedUsers" )&&
    sysGuardPopupData &&
     ( country || countryName )
  ) {
    return (
      <div className="sys-guard-location-side-chart">
        <SysGuardCountryMap
          mapData={sysGuardPopupData}
          countryCode={getCountryCode(country || countryName)}
        />
      </div>
    );
  } else if (
    location === "applicationBasedUsers" &&
    sysGuardPopupData &&
    sidePopupData
  ) {
    return (
      <div className="sys-guard-location-side-chart application">
        {renderSidePopupData(sidePopupData, location, isIncognito)}
        <SysGuardLineGraph popupData={sysGuardPopupData} />
      </div>
    );
  } else {
    return (
      <div
        className={
          location !== "locationBasedAnomalies" && location !== "cityBasedAnomalies"
            ? "sys-guard-location-side-chart application"
            : "sys-guard-location-side-chart location"
        }
      >
        {renderSidePopupData(sidePopupData, location, isIncognito)}
        <div>
          <SysGuardAlertButtons
            alertData={alertInfoData}
            dataSourceId={dataSourceId}
          />
          <SysGuardInfoBox
            data={alertInfoData}
            loc={location}
            parentData={parentData}
          />
        </div>
      </div>
    );
  }
};

function UserLocationPopup({
  popupOpen,
  onClose,
  popupData,
  sysGuardPopupData,
  totalElements,
  pagination,
  setPagination,
  tableColumns,
  sidePopupData,
  loc,
  dataSourceId,
  sysGuardTableLoader,
  countryName,
}) {
  const dispatch = useDispatch();
  const isSmall = useMediaQuery("(max-width:1600px)");
  const alertInfoData = useSelector(
    (state) => state.SysGuardReducer.alertInfoData,
  );
  const isIncognito = useSelector((state) => state.SysGuardReducer.incognito);
  const sysGuardDataSource = useSelector(
    (state) => state.SysGuardReducer.sysGuardDataSource,
  );
  const loader = useSelector((state) => state.alertStatusChanged.loader);
  return (
    <div className={popupOpen ? "sys-guard-backdrop" : ""}>
      <Popup
        open={popupOpen}
        onClose={onClose}
        modal
        closeOnDocumentClick={false}
        contentStyle={{ width: "auto" }}
      >
        {(loader || sysGuardTableLoader) && (
          <div className="global-loader-holder">
            <img
              src={Page_Loader}
              alt="loading"
              className="sysGuardPopUpLoader"
            />
          </div>
        )}
        <div className="sysguard-popup-container">
          <div className="user-location-container">
            <UserLocationHeader
              popupData={popupData}
              countryName = {countryName}
              loc={loc}
              isIncognito={isIncognito}
              sysGuardDataSource={sysGuardDataSource}
            />
            {isEmpty(sysGuardPopupData) || isNull(sysGuardPopupData) ? (
              <div>No Data Available</div>
            ) : (
              <>
                <div
                  className={
                    loc === "applicationBasedUsers"
                      ? "second-container application"
                      : "second-container"
                  }
                >
                  <UserLocationTable
                    sysGuardPopupData={sysGuardPopupData}
                    totalElements={totalElements}
                    pagination={pagination}
                    setPagination={setPagination}
                    tableColumns={tableColumns}
                    dispatch={dispatch}
                    loc={loc}
                    isSmall={isSmall}
                  />
                  <div className="right-side-panel">
                    <div className="secondary-title">
                      {renderSidePopupDataTitle(loc)}
                    </div>
                    <div>
                      {renderSideCharts(
                        loc,
                        sidePopupData,
                        sysGuardPopupData,
                        popupData?.countryName,
                        alertInfoData,
                        isIncognito,
                        dataSourceId,
                        popupData,
                        countryName,
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <button className="sys-guard-popup-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
      </Popup>
    </div>
  );
}

export default UserLocationPopup;
